import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Sparkles, Loader2, KeyRound, Mail, RefreshCw, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function maskEmail(email) {
  if (!email || !email.includes("@")) return email;
  const [local, domain] = email.split("@");
  const visible = local.length <= 2 ? local[0] : local.slice(0, 2);
  return `${visible}${"•".repeat(6)}@${domain}`;
}

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mockOtp, setMockOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [lastDelivery, setLastDelivery] = useState(null);
  const [countdown, setCountdown] = useState(0); // seconds remaining
  const timerRef = useRef(null);

  // Start 30-min countdown whenever an OTP is sent
  const startCountdown = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCountdown(30 * 60);
    timerRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const fmtCountdown = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const sendOtp = async (isResend = false) => {
    if (!email) { toast.error("Email required"); return; }
    if (isResend) setResending(true); else setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setLastDelivery({ delivered: data.delivered, provider: data.provider, expires_at: data.expires_at });
      startCountdown();
      if (data.delivered) {
        setMockOtp("");
        toast.success(isResend ? "New OTP sent — check your inbox" : "6-digit code sent to your email");
      } else if (data.mocked_otp) {
        setMockOtp(data.mocked_otp);
        toast.warning("Email couldn't be delivered — using dev OTP shown below");
      } else {
        toast.success("If the account exists, an OTP was sent.");
      }
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally {
      setLoading(false);
      setResending(false);
    }
  };

  const requestOtp = (e) => { e.preventDefault(); sendOtp(false); };
  const resendOtp = () => sendOtp(true);

  const reset = async (e) => {
    e.preventDefault();
    if (!otp || !newPassword) { toast.error("OTP and new password required"); return; }
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, otp, new_password: newPassword });
      toast.success("Password reset — signing in now…");
      nav("/signin", { state: { email } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired OTP. Try again or resend.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "var(--bg)" }} data-testid="forgot-page">
      <Card className="w-full max-w-md p-8 shadow-lg" style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="forgot-card">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ background: "var(--brand)" }}><Sparkles className="w-5 h-5" /></div>
          <span className="display text-xl">LaunchDeck</span>
        </div>
        <h2 className="display text-3xl mt-6 mb-1">Reset password</h2>
        <p className="text-sm mb-6" style={{ color: "var(--text-soft)" }}>
          {step === 1 ? "Enter your email — we'll send a 6-digit code." : "Enter the code and choose a new password."}
        </p>

        {step === 1 ? (
          <form onSubmit={requestOtp} className="space-y-4">
            <div><Label>Email</Label><Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoFocus /></div>
            <Button data-testid="forgot-request-otp" type="submit" disabled={loading} className="w-full gap-2 text-white" style={{ background: "var(--brand)" }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send OTP
            </Button>
          </form>
        ) : (
          <form onSubmit={reset} className="space-y-4">
            {lastDelivery?.delivered && (
              <div className="p-3 rounded-md flex items-start gap-2 text-sm" style={{ background: "color-mix(in srgb, #10B981 14%, transparent)", color: "var(--text)" }} data-testid="delivery-info">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />
                <div>
                  <div className="font-semibold">Code sent via {lastDelivery.provider}</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--text-soft)" }}>
                    Delivered to <span className="mono">{maskEmail(email)}</span> · <strong style={{ color: "var(--text)" }}>Check spam folder</strong> if not visible in 60 seconds.
                  </div>
                </div>
              </div>
            )}
            {mockOtp && (
              <div className="p-3 rounded-md text-sm" style={{ background: "color-mix(in srgb, var(--warn) 12%, transparent)" }} data-testid="mock-otp-display">
                <div className="text-xs uppercase tracking-wider font-semibold mono mb-1 flex items-center gap-1" style={{ color: "var(--warn)" }}>
                  <AlertTriangle className="w-3.5 h-3.5" /> Dev OTP (email delivery failed)
                </div>
                <div className="display text-3xl tracking-widest mono">{mockOtp}</div>
                <div className="text-xs mt-1" style={{ color: "var(--text-soft)" }}>Verify your sender domain in Settings for real deliverability.</div>
              </div>
            )}
            <div>
              <Label>OTP code</Label>
              <Input data-testid="forgot-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" inputMode="numeric" maxLength={6} className="mono text-lg tracking-widest text-center" autoFocus />
            </div>
            <div>
              <Label>New password</Label>
              <div className="relative">
                <Input data-testid="forgot-new-password" type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" minLength={6} className="pr-10" />
                <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute inset-y-0 right-0 flex items-center pr-3" style={{ color: "var(--text-soft)" }} tabIndex={-1} aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button data-testid="forgot-reset-submit" type="submit" disabled={loading} className="w-full gap-2 text-white" style={{ background: "var(--brand)" }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Reset password
            </Button>
            {countdown > 0 && (
              <div className="flex items-center justify-center gap-1.5 text-xs py-1" style={{ color: countdown < 60 ? "var(--warn)" : "var(--text-soft)" }} data-testid="otp-countdown">
                <span className="mono font-semibold">{fmtCountdown(countdown)}</span>
                <span>{countdown < 60 ? "— expires soon! Resend now" : "until code expires"}</span>
              </div>
            )}
            {countdown === 0 && step === 2 && (
              <div className="flex items-center justify-center gap-1.5 text-xs py-1 font-semibold" style={{ color: "var(--warn)" }} data-testid="otp-expired-notice">
                Code expired — click Resend to get a new one
              </div>
            )}
            <div className="flex items-center justify-between text-xs pt-2">
              <button type="button" onClick={() => setStep(1)} className="hover:underline" style={{ color: "var(--text-soft)" }} data-testid="forgot-change-email">← Change email</button>
              <button type="button" onClick={resendOtp} disabled={resending} className="inline-flex items-center gap-1 hover:underline disabled:opacity-50" style={{ color: "var(--brand)" }} data-testid="forgot-resend-otp">
                {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Resend OTP
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center text-sm">
          <Link to="/signin" className="hover:underline" style={{ color: "var(--brand)" }}>← Back to sign in</Link>
        </div>
      </Card>
    </div>
  );
}
